import { FormattedMessage } from 'react-intl'
import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import AnalyticsDetailCard from '@components/Analytics/DetailCard/DetailCard'
import { isNumber } from '@components/Analytics/PrePurchase/PrePurchase'
import { Hr } from '@components/UI/Hr/Hr.styled'
import { AnalyticsCustomPrice } from '@components/UI/Price/Price'
import { CPM_EXPLAINED, TRIBE_PRICING_PAGE_POST_PURCHASE_METRICS_UTM } from '@constants'
import { IApplicationState } from '@store'
import { selectSubmissionPerformance } from '@store/performance'
import { selectSubmissionId } from '@store/router'
import { selectSubmission, selectSubTotalCurrency } from '@store/submissions'
import Theme from '@theme'
import { Link } from '@tribegroup'
import { abbreviateNumberOrDash } from '@utils'
import { AnalyticsPostPerformanceGrid } from '../PostPerformance.styled'

const externalLink = TRIBE_PRICING_PAGE_POST_PURCHASE_METRICS_UTM

const AnalyticsPostPerformanceVideo = () => {
  const match = useRouteMatch()

  const { performance, followers, currency } = useSelector((state: IApplicationState) => {
    const submissionId = selectSubmissionId(match) as number

    const { identity_followers_count } = selectSubmission(state, submissionId)

    return {
      followers: identity_followers_count,
      currency: selectSubTotalCurrency(state, submissionId),
      performance: selectSubmissionPerformance(state, submissionId),
    }
  }, shallowEqual)

  const cpm = performance.cost_per_mille
  const cpv = performance.cost_per_view
  const reachPct = performance.reach_pct
  const viewPct = performance.view_rate

  return (
    <>
      <AnalyticsPostPerformanceGrid>
        <AnalyticsDetailCard
          title="submission.postPerformance.video.videoViews"
          content={abbreviateNumberOrDash(performance?.views)}
          elementName="post-performance-video-views"
          productMetricGroup="post_purchase"
          productMetricValue="video_views"
          externalLink={externalLink}
          featured
        />

        {cpv && (
          <AnalyticsDetailCard
            title="submission.postPerformance.video.CPV"
            content={isNumber(cpv) && <AnalyticsCustomPrice currency={currency} quantity={cpv} />}
            elementName="post-performance-cpv"
            productMetricGroup="post_purchase"
            productMetricValue="cpv"
            externalLink={externalLink}
            featured
          />
        )}

        <AnalyticsDetailCard
          title="submission.postPerformance.video.impressions"
          content={abbreviateNumberOrDash(performance?.impressions)}
          elementName="post-performance-impressions"
          productMetricGroup="post_purchase"
          productMetricValue="impressions"
          externalLink={externalLink}
          featured
        />
      </AnalyticsPostPerformanceGrid>
      <Hr backgroundColor={Theme.grey400Color} />
      <AnalyticsPostPerformanceGrid topOuterSpacing={1.75}>
        <AnalyticsDetailCard
          title="submission.postPerformance.followers"
          content={abbreviateNumberOrDash(followers)}
          elementName="post-performance-followers"
          productMetricGroup="post_purchase"
          productMetricValue="followers"
          externalLink={externalLink}
        />

        <AnalyticsDetailCard
          title="submission.postPerformance.video.videoViewsPercentage"
          content={
            isNumber(viewPct) && (
              <FormattedMessage id="core.text.percent" values={{ value: viewPct }} />
            )
          }
          elementName="post-performance-video-views-percentage"
          productMetricGroup="post_purchase"
          productMetricValue="engagement_pct"
          externalLink={externalLink}
        />

        <AnalyticsDetailCard
          title="submission.postPerformance.video.reach"
          content={abbreviateNumberOrDash(performance?.reach)}
          elementName="post-performance-reach"
          productMetricGroup="post_purchase"
          productMetricValue="reach"
          externalLink={externalLink}
        />
        <AnalyticsDetailCard
          title="submission.postPerformance.video.reachPercentage"
          content={
            isNumber(reachPct) && (
              <FormattedMessage id="core.text.percent" values={{ value: reachPct }} />
            )
          }
          elementName="post-performance-reach-percentage"
          productMetricGroup="post_purchase"
          productMetricValue="reach_pct"
          externalLink={externalLink}
        />
        {cpm && (
          <AnalyticsDetailCard
            title="submission.postPerformance.video.CPM"
            content={isNumber(cpm) && <AnalyticsCustomPrice currency={currency} quantity={cpm} />}
            elementName="post-performance-cpm"
            productMetricGroup="post_purchase"
            productMetricValue="cpm"
            externalLink={externalLink}
            values={{
              link: (
                <Link href={CPM_EXPLAINED} target="_blank" color={Theme.primaryColor} lowercase>
                  <FormattedMessage id="core.text.clickHere" />
                </Link>
              ),
            }}
          />
        )}
      </AnalyticsPostPerformanceGrid>
    </>
  )
}

export { AnalyticsPostPerformanceVideo }

export default AnalyticsPostPerformanceVideo
